/* eslint-disable no-empty */
/**
 * audioFxPresets.js — Web Audio FX preset library used by the Director's
 * mixer rack to apply real-time effects to ANY mic channel (host mic,
 * mic 2, future guest mics). Each preset describes an ordered chain of
 * Web Audio nodes; `buildFxChain(ctx, key)` mints a fresh chain for the
 * given AudioContext, returns the head + tail + array of nodes so the
 * caller can splice it between source and the channel's GainNode.
 *
 * The chains are designed to sound musical out of the box — every
 * preset has a -3 to -6 dB make-up gain at the end so it doesn't blow
 * out the master bus when the host hot-swaps to a heavier preset
 * mid-broadcast.
 */

// Synth a 1.6s "small room" impulse response on demand (no external file).
// Cached per AudioContext so we only mint it once per session.
const irCache = new WeakMap();
function makeReverbIR(ctx, durationSec = 1.6, decay = 2.4) {
  const cached = irCache.get(ctx);
  if (cached) return cached;
  const rate = ctx.sampleRate;
  const length = Math.floor(rate * durationSec);
  const ir = ctx.createBuffer(2, length, rate);
  for (let ch = 0; ch < 2; ch++) {
    const data = ir.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      // Decaying noise tail — classic algorithmic plate-like impulse.
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
    }
  }
  irCache.set(ctx, ir);
  return ir;
}

const PRESETS = {
  off:        { label: "Clean",     description: "Pass-through, no effect." },
  podcast:    { label: "Podcast",   description: "High-pass + 3 kHz lift + light compression." },
  broadcast:  { label: "Broadcast", description: "Heavier compressor, 2-band EQ." },
  hype:       { label: "Hype",      description: "Compressor + +3 dB boost + slight delay tap." },
  echo:       { label: "Echo",      description: "1/4-second slap with 35% feedback." },
  reverb:     { label: "Reverb",    description: "Plate-style hall, 1.6s tail." },
  telephone:  { label: "Phone",     description: "300–3400 Hz bandpass — sounds like a call." },
  stadium:    { label: "Stadium",   description: "Long reverb + slight delay tap." },
  robot:      { label: "Robot",     description: "Tremolo modulator at 7 Hz." },
  helium:     { label: "Helium",    description: "Bright tilt + treble boost." },
  deep:       { label: "Deep",      description: "Sub-lift + low-pass, sounds bigger." },
};

export const FX_LABELS = Object.fromEntries(
  Object.entries(PRESETS).map(([k, v]) => [k, v.label]),
);

export const FX_KEYS = Object.keys(PRESETS);

/**
 * Build a chain for the given preset.
 * Returns { head, tail, nodes }.
 *   - head: connect your AudioSource here.
 *   - tail: connect this to your channel GainNode (or master).
 *   - nodes: the full ordered list — caller should disconnect()/null when
 *            swapping presets so we don't leak audio nodes.
 *
 * For "off" the head and tail are the same passthrough Gain so the call
 * site doesn't need a special case.
 */
export function buildFxChain(ctx, key) {
  const passthrough = ctx.createGain();
  passthrough.gain.value = 1;
  if (!key || key === "off") {
    return { head: passthrough, tail: passthrough, nodes: [passthrough] };
  }

  const nodes = [];
  let head = null;
  let tail = null;
  const link = (node) => {
    if (!head) { head = node; tail = node; nodes.push(node); return; }
    tail.connect(node);
    tail = node;
    nodes.push(node);
  };
  const makeup = (db) => {
    const g = ctx.createGain();
    g.gain.value = Math.pow(10, db / 20);
    link(g);
  };

  switch (key) {
    case "podcast": {
      const hp = ctx.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 90; link(hp);
      const peak = ctx.createBiquadFilter(); peak.type = "peaking"; peak.frequency.value = 3000; peak.gain.value = 4; peak.Q.value = 1; link(peak);
      const comp = ctx.createDynamicsCompressor(); comp.threshold.value = -22; comp.ratio.value = 3; comp.attack.value = 0.003; comp.release.value = 0.18; link(comp);
      makeup(2);
      break;
    }
    case "broadcast": {
      const hp = ctx.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 80; link(hp);
      const lo = ctx.createBiquadFilter(); lo.type = "lowshelf"; lo.frequency.value = 200; lo.gain.value = 2; link(lo);
      const hi = ctx.createBiquadFilter(); hi.type = "highshelf"; hi.frequency.value = 5000; hi.gain.value = 3; link(hi);
      const comp = ctx.createDynamicsCompressor(); comp.threshold.value = -16; comp.ratio.value = 5; comp.attack.value = 0.002; comp.release.value = 0.12; link(comp);
      makeup(3);
      break;
    }
    case "hype": {
      const comp = ctx.createDynamicsCompressor(); comp.threshold.value = -14; comp.ratio.value = 6; comp.attack.value = 0.002; comp.release.value = 0.1; link(comp);
      const peak = ctx.createBiquadFilter(); peak.type = "peaking"; peak.frequency.value = 4000; peak.gain.value = 5; peak.Q.value = 1.2; link(peak);
      // Tiny delay tap mixed in via a parallel gain so the original signal stays clear.
      // Implemented inline as a simple slap-tap delay summing back to tail.
      const tapIn = ctx.createGain(); tapIn.gain.value = 1; link(tapIn);
      const dry = ctx.createGain(); dry.gain.value = 1.0;
      const wetDelay = ctx.createDelay(1.0); wetDelay.delayTime.value = 0.09;
      const wetGain = ctx.createGain(); wetGain.gain.value = 0.18;
      tapIn.connect(dry); tapIn.connect(wetDelay); wetDelay.connect(wetGain);
      const sum = ctx.createGain(); sum.gain.value = 1;
      dry.connect(sum); wetGain.connect(sum);
      tail = sum; nodes.push(dry, wetDelay, wetGain, sum);
      makeup(3);
      break;
    }
    case "echo": {
      const dryIn = ctx.createGain(); dryIn.gain.value = 1; link(dryIn);
      const dry = ctx.createGain(); dry.gain.value = 1.0;
      const delay = ctx.createDelay(2.0); delay.delayTime.value = 0.25;
      const fb = ctx.createGain(); fb.gain.value = 0.35;
      delay.connect(fb).connect(delay);
      const wet = ctx.createGain(); wet.gain.value = 0.45;
      dryIn.connect(dry); dryIn.connect(delay); delay.connect(wet);
      const sum = ctx.createGain(); sum.gain.value = 1;
      dry.connect(sum); wet.connect(sum);
      tail = sum; nodes.push(dry, delay, fb, wet, sum);
      break;
    }
    case "reverb":
    case "stadium": {
      const conv = ctx.createConvolver(); conv.buffer = makeReverbIR(ctx, key === "stadium" ? 2.6 : 1.6, key === "stadium" ? 1.6 : 2.4);
      const wet = ctx.createGain(); wet.gain.value = key === "stadium" ? 0.55 : 0.4;
      // Build a dry+wet parallel mixer:
      //   head = wrap → (dry path → sum)
      //                → (conv → wet → sum)
      //   tail = sum
      const wrap = ctx.createGain(); wrap.gain.value = 1;
      const dryIn = ctx.createGain(); dryIn.gain.value = 1.0;
      const sum = ctx.createGain();
      head = wrap; tail = sum;
      wrap.connect(dryIn).connect(sum);
      wrap.connect(conv); conv.connect(wet).connect(sum);
      // We built the graph manually above — populate `nodes` so dispose
      // tears them all down.
      nodes.length = 0;
      nodes.push(wrap, dryIn, conv, wet, sum);
      makeup(2);
      break;
    }
    case "telephone": {
      const hp = ctx.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 300; link(hp);
      const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 3400; link(lp);
      const peak = ctx.createBiquadFilter(); peak.type = "peaking"; peak.frequency.value = 1500; peak.gain.value = 6; peak.Q.value = 0.8; link(peak);
      makeup(4);
      break;
    }
    case "robot": {
      // Tremolo: gain LFO at 7 Hz modulating amplitude between 0.4 and 1.
      const tremolo = ctx.createGain(); tremolo.gain.value = 0.7; link(tremolo);
      const lfo = ctx.createOscillator(); lfo.frequency.value = 7;
      const lfoDepth = ctx.createGain(); lfoDepth.gain.value = 0.35;
      lfo.connect(lfoDepth).connect(tremolo.gain);
      try { lfo.start(); } catch {}
      // Slight bandpass for that "speak-and-spell" sheen
      const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 1200; bp.Q.value = 1.5; link(bp);
      makeup(2);
      nodes.push(lfo, lfoDepth);
      break;
    }
    case "helium": {
      const tilt = ctx.createBiquadFilter(); tilt.type = "highshelf"; tilt.frequency.value = 1500; tilt.gain.value = 6; link(tilt);
      const lo = ctx.createBiquadFilter(); lo.type = "lowshelf"; lo.frequency.value = 200; lo.gain.value = -4; link(lo);
      makeup(1);
      break;
    }
    case "deep": {
      const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 6000; link(lp);
      const sub = ctx.createBiquadFilter(); sub.type = "lowshelf"; sub.frequency.value = 200; sub.gain.value = 5; link(sub);
      makeup(2);
      break;
    }
    default: {
      // Unknown preset — fall back to passthrough.
      return { head: passthrough, tail: passthrough, nodes: [passthrough] };
    }
  }
  return { head, tail, nodes };
}

export function disposeChain(chain) {
  if (!chain || !chain.nodes) return;
  for (const n of chain.nodes) {
    try { n.disconnect(); } catch {}
    if (n.stop) { try { n.stop(); } catch {} }
  }
  chain.nodes.length = 0;
  chain.head = chain.tail = null;
}
